import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, UserPlus, X } from "lucide-react";

const PRESETS = [
    { key: "7d", label: "7 Days" },
    { key: "14d", label: "14 Days" },
    { key: "30d", label: "30 Days" },
    { key: "3mo", label: "3 Months" },
    { key: "6mo", label: "6 Months" },
];

export const AddClientDialog = ({ onClose, onCreated, availableTags = [] }) => {
    const [form, setForm] = useState({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        expires_at: "",
        preset_duration: "",
        tag: "",
        monthly_script_limit: "",
    });
    const [saving, setSaving] = useState(false);
    const [result, setResult] = useState(null);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const pickPreset = (key) => {
        setForm((f) => ({ ...f, preset_duration: key, expires_at: "" }));
    };

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const { data } = await api.post("/admin/clients", {
                first_name: form.first_name.trim(),
                last_name: form.last_name.trim(),
                email: form.email.trim(),
                phone: form.phone.trim(),
                expires_at: form.expires_at || null,
                preset_duration: form.preset_duration || null,
                tag: form.tag.trim() || null,
                monthly_script_limit: form.monthly_script_limit ? parseInt(form.monthly_script_limit, 10) : null,
            });
            setResult(data);
            onCreated && onCreated();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to add client");
        } finally {
            setSaving(false);
        }
    };

    const copyCode = async () => {
        try {
            await navigator.clipboard.writeText(result.code);
            toast.success("Code copied");
        } catch {}
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-un-onyx/70"
            role="dialog"
            aria-modal="true"
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="un-card max-w-lg w-full bg-white max-h-[90vh] overflow-y-auto"
                data-testid="add-client-dialog"
            >
                <div className="flex items-center justify-between border-b-2 border-un-onyx px-6 py-4 sticky top-0 bg-white z-10">
                    <div className="un-label text-un-grape">Add One Client</div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="un-btn-ghost !px-1 !py-1"
                        aria-label="Close"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
                {result ? (
                    <div className="p-6">
                        <div className="un-label text-un-grape mb-3">Client Added — Share This Code</div>
                        <div
                            className="un-mono text-2xl font-bold bg-un-lime text-un-onyx border-2 border-un-onyx px-4 py-3 mb-4 tracking-wider"
                            data-testid="add-client-code"
                        >
                            {result.code}
                        </div>
                        <p className="text-sm text-un-onyx/70 mb-2">
                            Send this activation code to <strong>{result.intended_email}</strong>.
                        </p>
                        {result.expires_at && (
                            <p className="text-sm text-un-onyx/70 mb-6">
                                Access expires: <strong>{new Date(result.expires_at).toLocaleDateString()}</strong>
                            </p>
                        )}
                        <div className="flex items-center justify-end gap-3">
                            <button onClick={copyCode} className="un-btn-outline text-sm">
                                Copy Code
                            </button>
                            <button onClick={onClose} className="un-btn-primary text-sm">
                                Done
                            </button>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={submit} className="p-6 space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <label className="block">
                                <span className="un-label text-un-onyx block mb-2">First Name</span>
                                <input
                                    type="text"
                                    required
                                    value={form.first_name}
                                    onChange={set("first_name")}
                                    className="un-input"
                                    data-testid="add-client-first-name"
                                />
                            </label>
                            <label className="block">
                                <span className="un-label text-un-onyx block mb-2">Last Name</span>
                                <input
                                    type="text"
                                    required
                                    value={form.last_name}
                                    onChange={set("last_name")}
                                    className="un-input"
                                    data-testid="add-client-last-name"
                                />
                            </label>
                        </div>
                        <label className="block">
                            <span className="un-label text-un-onyx block mb-2">Email</span>
                            <input
                                type="email"
                                required
                                value={form.email}
                                onChange={set("email")}
                                className="un-input"
                                data-testid="add-client-email"
                            />
                        </label>
                        <label className="block">
                            <span className="un-label text-un-onyx block mb-2">Phone (Optional)</span>
                            <input
                                type="tel"
                                value={form.phone}
                                onChange={set("phone")}
                                className="un-input"
                                data-testid="add-client-phone"
                            />
                        </label>
                        <label className="block">
                            <span className="un-label text-un-onyx block mb-2">Tag (Optional)</span>
                            <input
                                type="text"
                                list="add-client-tags"
                                value={form.tag}
                                onChange={set("tag")}
                                className="un-input"
                                placeholder="Pick or type new"
                                data-testid="add-client-tag"
                            />
                            <datalist id="add-client-tags">
                                {availableTags.map((t) => (
                                    <option key={t.id} value={t.name} />
                                ))}
                            </datalist>
                        </label>

                        <div>
                            <span className="un-label text-un-onyx block mb-2">Access Duration</span>
                            <div className="flex flex-wrap gap-2 mb-3">
                                {PRESETS.map((p) => (
                                    <button
                                        key={p.key}
                                        type="button"
                                        onClick={() => pickPreset(p.key)}
                                        className={`text-xs font-bold px-3 py-2 border-2 border-un-onyx ${form.preset_duration === p.key ? "bg-un-berry text-white" : "bg-white text-un-onyx hover:bg-un-onyx hover:text-white"}`}
                                        data-testid={`preset-${p.key}`}
                                    >
                                        {p.label}
                                    </button>
                                ))}
                            </div>
                            <label className="block">
                                <span className="un-label text-un-onyx block mb-2 text-xs">Or Pick A Custom Date</span>
                                <input
                                    type="date"
                                    value={form.expires_at}
                                    onChange={(e) => setForm((f) => ({ ...f, expires_at: e.target.value, preset_duration: "" }))}
                                    className="un-input"
                                    min={new Date().toISOString().split("T")[0]}
                                    data-testid="add-client-expires"
                                />
                            </label>
                        </div>

                        <label className="block">
                            <span className="un-label text-un-onyx block mb-2">Monthly Generation Limit (Optional)</span>
                            <input
                                type="number"
                                min="0"
                                value={form.monthly_script_limit}
                                onChange={(e) => setForm((f) => ({ ...f, monthly_script_limit: e.target.value }))}
                                className="un-input"
                                placeholder="Leave blank for unlimited"
                                data-testid="add-client-monthly-limit"
                            />
                            <span className="text-xs text-un-onyx/60 mt-1 block">
                                Max generations this client can run per calendar month. Each generation produces 3 variants. Blank = unlimited. Resets on the 1st.
                            </span>
                        </label>

                        <div className="flex items-center justify-end gap-3 pt-2">
                            <button type="button" onClick={onClose} className="un-btn-outline text-sm">
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={saving}
                                className="un-btn-primary text-sm flex items-center gap-2"
                                data-testid="add-client-submit"
                            >
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                                Add & Generate Code
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};
